// Reading a family's notes wherever they live.
//
// THE PROBLEM. Notes attach to the record they were written on, and a
// household's records are spread across six kinds: the household, each
// person, each policy/loan/case/portfolio, each opportunity, each
// processing record, each ongoing-care cycle. Every Notes surface in the
// OS showed exactly one of those and admitted nothing else existed. After
// the 2026-08-11 ActiveCampaign import (163,919 notes, ~60 per household,
// most of them on CLOSED opportunities and processing records), the note
// you need is usually on a record you would never think to open. Barbara
// Fournier's Medicare policy page said "No notes" while the note about her
// doctors and medications sat on the won opportunity behind it.
//
// TWO SURFACES, ONE ENDPOINT.
//   * HouseholdNoteCenter — the canonical reader, on the household's
//     Notes tab. Filter chips per record kind, search, pagination.
//   * RelatedHouseholdNotes — the compact module that sits under the
//     record's OWN notes on every record page. Top few + honest count +
//     one click into the Center. Deliberately NOT a second full archive:
//     a working page should stay a working page.
//
// Both are READ-ONLY. Nothing here moves, copies, or edits a note — a
// note's home stays the record it was written on, and every row links
// back to that record. Editing happens there, where the context is.
//
// WHAT IS DELIBERATELY ABSENT: inferred relevance ranking and extracted
// metadata chips ("medications", "underwriting"). Nothing in 163k free-text
// notes computes those honestly, and a misfiled note is worse than a
// hard-to-find one — the reader stops looking. The only relevance signal
// applied is `pinned`/`important`, which a human actually set.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Pin, Search, Star } from "lucide-react";

import {
  api,
  ApiError,
  type HouseholdNote,
  type HouseholdNoteKind,
  type HouseholdNotesPage,
} from "../api";
import { Button, Card } from "../ui";
import { subscribeNotesChanged } from "./notes/notes-changed-event";

export interface HouseholdNoteAnchor {
  entityType: HouseholdNoteKind;
  entityId: string;
}

// How a record kind is named to a user, and where its detail page lives.
// Plain-English labels per the vocabulary lock — "Processing", not
// "transaction"; "Ongoing care", not "rhythm instance".
const KIND_META: Record<
  HouseholdNoteKind,
  { label: string; plural: string; path: (id: string) => string }
> = {
  household: {
    label: "Household",
    plural: "Household",
    path: (id) => `/households/${encodeURIComponent(id)}`,
  },
  contact: {
    label: "Person",
    plural: "People",
    path: (id) => `/contacts/${encodeURIComponent(id)}`,
  },
  service_record: {
    label: "Policy / loan / case",
    plural: "Policies & loans",
    path: (id) => `/service-records/${encodeURIComponent(id)}`,
  },
  opportunity: {
    label: "Opportunity",
    plural: "Opportunities",
    path: (id) => `/opportunities/${encodeURIComponent(id)}`,
  },
  transaction: {
    label: "Processing",
    plural: "Processing",
    path: (id) => `/processing/${encodeURIComponent(id)}`,
  },
  rhythm_instance: {
    label: "Ongoing care",
    plural: "Ongoing care",
    path: (id) => `/ongoing-care/${encodeURIComponent(id)}`,
  },
};

// Chip order — the kinds advisors reach for most, first.
const FILTER_ORDER: HouseholdNoteKind[] = [
  "contact",
  "opportunity",
  "transaction",
  "service_record",
  "rhythm_instance",
  "household",
];

const CENTER_PAGE_SIZE = 25;
const MODULE_PAGE_SIZE = 3;
// Long notes collapse to a preview. Roughly three lines of prose — enough
// to tell whether this is the note you're looking for.
const PREVIEW_CHARS = 260;

// ─── The shared row ───────────────────────────────────────────────────

function NoteOriginLine({ note }: { note: HouseholdNote }) {
  const meta = KIND_META[note.entity_type];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 6 }}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--dark-gray)",
        }}
      >
        {meta.label}
      </span>
      <Link
        to={meta.path(note.entity_id)}
        style={{ fontSize: 13, fontWeight: 600 }}
        data-testid="household-note-origin-link"
      >
        {note.origin_label}
      </Link>
      {note.origin_detail && (
        <span className="muted" style={{ fontSize: 12 }}>
          · {humanizeDetail(note.origin_detail)}
        </span>
      )}
    </div>
  );
}

export function HouseholdNoteRow({
  note,
  onEditHere,
}: {
  note: HouseholdNote;
  /** When set, the row offers "Edit or pin" — used by the notes dock for a
   *  note that lives on the record being viewed, since this household-wide
   *  list is read-only and the dock's "This record" view is where editing
   *  happens. */
  onEditHere?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isLong = note.body.length > PREVIEW_CHARS;
  const shown = expanded || !isLong ? note.body : `${note.body.slice(0, PREVIEW_CHARS).trimEnd()}…`;
  const classes = [
    "household-tab-note",
    note.pinned ? "is-pinned" : "",
    note.important && !note.pinned ? "is-important" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <li className={classes} data-testid="household-note-row">
      {/* Pinned/important notes float to the top of this household-wide
          list, same as they do on their own record's Notes section — this
          says why, so it isn't a mystery why one note leads a family's
          whole history. */}
      {(note.pinned || note.important) && (
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          {note.pinned && (
            <span className="household-tab-note-pill">
              <Pin size={14} strokeWidth={1.5} aria-hidden /> Pinned
            </span>
          )}
          {note.important && (
            <span className="household-tab-note-pill is-important">
              <Star size={14} strokeWidth={1.5} fill="currentColor" aria-hidden /> Important
            </span>
          )}
        </div>
      )}
      <NoteOriginLine note={note} />
      {note.title && (
        <div style={{ fontWeight: 600, fontSize: 13.5, marginTop: 6 }}>{note.title}</div>
      )}
      <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 6, whiteSpace: "pre-wrap" }}>
        {shown}
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            marginTop: 4,
            color: "var(--blue)",
            fontSize: 12.5,
            cursor: "pointer",
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
      <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        {note.author_name ?? "Unknown author"} · {formatNoteDate(note.created_at)}
        {note.source === "migration" && " · imported from ActiveCampaign"}
        {onEditHere && (
          <>
            {" · "}
            <button
              type="button"
              onClick={onEditHere}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "var(--blue)",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Edit or pin
            </button>
          </>
        )}
      </div>
    </li>
  );
}

// ─── Shared fetching ──────────────────────────────────────────────────

interface FetchState {
  page: HouseholdNotesPage | null;
  loading: boolean;
  error: string | null;
}

export function useHouseholdNotes(
  anchor: HouseholdNoteAnchor,
  opts: { kinds: HouseholdNoteKind[]; q: string; limit: number; offset: number },
): FetchState & { reload: () => void } {
  const [state, setState] = useState<FetchState>({
    page: null,
    loading: true,
    error: null,
  });
  const { kinds, q, limit, offset } = opts;
  // Serialized so the effect re-runs on real filter changes, not on every
  // render's fresh array identity.
  const kindKey = kinds.join(",");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    const query: {
      kinds?: HouseholdNoteKind[];
      q?: string;
      limit: number;
      offset: number;
    } = { limit, offset };
    if (kindKey !== "") query.kinds = kindKey.split(",") as HouseholdNoteKind[];
    if (q !== "") query.q = q;
    api
      .listHouseholdNotes(anchor, query)
      .then((page) => {
        if (!cancelled) setState({ page, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          page: null,
          loading: false,
          error:
            err instanceof ApiError ? err.message : "Couldn't load the household's other notes.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [anchor.entityType, anchor.entityId, kindKey, q, limit, offset, reloadTick]);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);
  return { ...state, reload };
}

// ─── The compact in-record module ─────────────────────────────────────

/**
 * "More notes elsewhere in this household" — mounts directly under a
 * record's own Notes section, in the same place on every record page.
 *
 * Renders NOTHING when the household has no other notes, so a clean
 * record stays clean. Shows the newest few, an honest total, and one link
 * into the full reader; it is deliberately not a second archive.
 */
export function RelatedHouseholdNotes({
  anchor,
  householdName,
}: {
  anchor: HouseholdNoteAnchor;
  /** For the plain-English count line ("…in the Fournier household").
   *  Falls back to "this household" when the page doesn't know it. */
  householdName?: string | null | undefined;
}) {
  const [open, setOpen] = useState(false);
  const { page, loading, error } = useHouseholdNotes(anchor, {
    kinds: [],
    q: "",
    limit: MODULE_PAGE_SIZE,
    offset: 0,
  });

  // Silent while loading and silent on failure: this is a supplementary
  // panel, and an error banner here would shout over the record's own
  // notes, which loaded fine.
  if (loading || error || !page || page.total === 0 || !page.household_id) return null;

  const where = householdName ? `the ${householdName} household` : "this household";
  const centerHref = `/households/${encodeURIComponent(page.household_id)}?tab=notes`;

  return (
    <Card className="household-tab-card" data-testid="related-household-notes">
      <header className="household-tab-card-header">
        <div>
          <h2 style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                font: "inherit",
                color: "inherit",
              }}
            >
              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              Notes elsewhere in this household
            </button>
          </h2>
          <p className="household-tab-card-sub">
            {page.total} {page.total === 1 ? "note lives" : "notes live"} on other records in{" "}
            {where} — people, opportunities, processing, policies and care plans. They stay where
            they were written; this is a read-only view.
          </p>
        </div>
      </header>

      {open && (
        <>
          <ul className="household-tab-note-list">
            {page.notes.map((n) => (
              <HouseholdNoteRow key={n.id} note={n} />
            ))}
          </ul>
          {page.total > page.notes.length && (
            <p style={{ fontSize: 12.5, marginTop: 10 }}>
              Showing the {page.notes.length} most recent of {page.total}.{" "}
              <Link to={centerHref}>Read all notes for this household →</Link>
            </p>
          )}
          {page.total <= page.notes.length && (
            <p style={{ fontSize: 12.5, marginTop: 10 }}>
              <Link to={centerHref}>Read all notes for this household →</Link>
            </p>
          )}
        </>
      )}
    </Card>
  );
}

// ─── The count, and the "Latest notes" strip ──────────────────────────

/**
 * How many notes this whole household has, across every record. The number
 * the Notes tab badge and the dock tab show.
 *
 * It is the reader endpoint's COUNT(*) over the household, never the length
 * of a list a page happened to load (CLAUDE.md "Accuracy"). Asking for one
 * row keeps it cheap. It refetches when a note changes on the anchor record,
 * so saving a note moves the badge without a reload.
 */
export function useHouseholdNoteTotal(anchor: HouseholdNoteAnchor | null): {
  total: number | null;
  householdId: string | null;
} {
  const [state, setState] = useState<{ total: number | null; householdId: string | null }>({
    total: null,
    householdId: null,
  });
  const [tick, setTick] = useState(0);
  const entityType = anchor?.entityType;
  const entityId = anchor?.entityId;

  useEffect(() => {
    if (!entityType || !entityId) return;
    return subscribeNotesChanged({ entityType, entityId }, () => setTick((t) => t + 1));
  }, [entityType, entityId]);

  useEffect(() => {
    if (!entityType || !entityId) return;
    let cancelled = false;
    api
      .listHouseholdNotes({ entityType, entityId }, { limit: 1, offset: 0 })
      .then((page) => {
        if (!cancelled) setState({ total: page.total, householdId: page.household_id });
      })
      .catch(() => {
        // A count is a nicety; on failure the badge simply stays absent.
        if (!cancelled) setState({ total: null, householdId: null });
      });
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, tick]);

  return state;
}

/**
 * "Latest notes" — the newest few notes written about this household,
 * wherever they live, with an honest total and one click into the full
 * Notes tab. This is the ActiveCampaign habit (scroll to the bottom, read
 * the notes) put back: it mounts at the bottom of a case or policy page
 * and on the household Overview.
 *
 * Read-only, like every household-wide reader: a note's home stays the
 * record it was written on, and each row links there. Renders a plain
 * "no notes yet" line rather than nothing, because an absent section is
 * exactly what made people unsure whether notes existed.
 */
export function LatestHouseholdNotes({
  anchor,
  householdName,
  limit = 5,
}: {
  anchor: HouseholdNoteAnchor;
  householdName?: string | null | undefined;
  limit?: number;
}) {
  const { page, loading, error, reload } = useHouseholdNotes(anchor, {
    kinds: [],
    q: "",
    limit,
    offset: 0,
  });

  // Stay in step with the dock: a note saved or deleted on the record in
  // view should show here without a reload.
  useEffect(
    () => subscribeNotesChanged({ entityType: anchor.entityType, entityId: anchor.entityId }, reload),
    [anchor.entityType, anchor.entityId, reload],
  );

  const where = householdName ? `the ${householdName} household` : "this household";
  const allHref = page?.household_id
    ? `/households/${encodeURIComponent(page.household_id)}?tab=notes`
    : null;

  return (
    <Card className="household-tab-card" data-testid="latest-household-notes">
      <header className="household-tab-card-header">
        <div>
          <h2>Latest notes</h2>
          <p className="household-tab-card-sub">
            {page
              ? page.total === 0
                ? `No notes have been written about ${where} yet.`
                : `The newest ${page.notes.length} of ${page.total} ${
                    page.total === 1 ? "note" : "notes"
                  } on ${where}, from every record. To add one, open the Notes tab at the bottom of the screen.`
              : `Notes written about ${where}, from every record.`}
          </p>
        </div>
      </header>

      {loading && !page && <p className="muted">Loading…</p>}
      {error && (
        <p style={{ fontSize: 13, color: "var(--danger)" }} role="status">
          {error}
        </p>
      )}

      {page && page.notes.length > 0 && (
        <ul className="household-tab-note-list">
          {page.notes.map((n) => (
            <HouseholdNoteRow key={n.id} note={n} />
          ))}
        </ul>
      )}

      {allHref && (
        <p style={{ fontSize: 12.5, marginTop: 10 }}>
          <Link to={allHref}>
            {page && page.total > page.notes.length
              ? `See all ${page.total} notes, plus meetings and emails →`
              : "Open the household Notes tab (meetings and emails too) →"}
          </Link>
        </p>
      )}
    </Card>
  );
}

// ─── The full reader ──────────────────────────────────────────────────

/**
 * The Household Note Center — the canonical place to read a family's
 * whole history across every record it owns. Lives below the household's
 * own notes on the household page's Notes tab (which keeps the composer,
 * since writing still goes to the household).
 */
export function HouseholdNoteCenter({ householdId }: { householdId: string }) {
  const anchor = useMemo<HouseholdNoteAnchor>(
    () => ({ entityType: "household" as const, entityId: householdId }),
    [householdId],
  );
  const [kinds, setKinds] = useState<HouseholdNoteKind[]>([]);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);

  const { page, loading, error } = useHouseholdNotes(anchor, {
    kinds,
    q: search,
    limit: CENTER_PAGE_SIZE,
    offset,
  });

  // Any filter change restarts paging — otherwise page 3 of "everything"
  // becomes an empty page 3 of "opportunities only", which reads as
  // "there are none" when there are plenty.
  function applyKinds(next: HouseholdNoteKind[]) {
    setKinds(next);
    setOffset(0);
  }
  function applySearch(next: string) {
    setSearch(next);
    setOffset(0);
  }

  const countsByKind = page?.counts_by_kind;
  // Total across all kinds, independent of the current chip selection —
  // this is the "All" chip's number, and it must not be measured off the
  // fetched page.
  const allCount = countsByKind
    ? FILTER_ORDER.reduce((sum, k) => sum + (countsByKind[k] ?? 0), 0)
    : 0;

  return (
    <Card className="household-tab-card" data-testid="household-note-center">
      <header className="household-tab-card-header">
        <div>
          <h2>Everything else written about this household</h2>
          <p className="household-tab-card-sub">
            Notes saved on this family&rsquo;s people, opportunities, processing records, policies
            and loans, and care plans — gathered here so you don&rsquo;t have to open each record to
            find them. Each note still lives on its own record; click through to edit it there.
          </p>
        </div>
      </header>

      {/* Filter chips. Each carries its own honest count, so switching
          filters never surprises you with a number you didn't expect. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        <FilterChip
          label="All"
          count={allCount}
          active={kinds.length === 0}
          onClick={() => applyKinds([])}
        />
        {FILTER_ORDER.map((kind) => {
          const count = countsByKind?.[kind] ?? 0;
          if (count === 0) return null;
          const active = kinds.includes(kind);
          return (
            <FilterChip
              key={kind}
              label={KIND_META[kind].plural}
              count={count}
              active={active}
              onClick={() =>
                applyKinds(active ? kinds.filter((k) => k !== kind) : [...kinds, kind])
              }
            />
          );
        })}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          applySearch(searchDraft.trim());
        }}
        style={{ display: "flex", gap: 6, marginBottom: 14 }}
      >
        <div style={{ position: "relative", flex: 1 }}>
          <Search
            size={15}
            style={{
              position: "absolute",
              left: 9,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--dark-gray)",
            }}
          />
          <input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search these notes — a medication, a carrier, a name…"
            aria-label="Search this household's notes"
            style={{
              width: "100%",
              fontSize: 13,
              padding: "8px 10px 8px 30px",
              border: "1px solid var(--mid-gray)",
              borderRadius: "var(--radius-md)",
            }}
          />
        </div>
        <Button variant="sw-primary" type="submit">
          Search
        </Button>
        {search !== "" && (
          <Button
            size="sm"
            onClick={() => {
              setSearchDraft("");
              applySearch("");
            }}
          >
            Clear
          </Button>
        )}
      </form>

      {loading && <p className="muted">Loading…</p>}
      {error && (
        <p style={{ fontSize: 13, color: "var(--danger)" }} role="status">
          {error}
        </p>
      )}

      {!loading && !error && page && page.total === 0 && (
        <p className="muted" style={{ fontSize: 13 }}>
          {search !== "" || kinds.length > 0
            ? "No notes match that filter. Clear it to see everything."
            : "Nothing yet — no notes are saved on this household's other records."}
        </p>
      )}

      {!loading && !error && page && page.notes.length > 0 && (
        <>
          <p className="muted" style={{ fontSize: 12.5, margin: "0 0 8px" }}>
            {rangeLine(page)}
          </p>
          <ul className="household-tab-note-list">
            {page.notes.map((n) => (
              <HouseholdNoteRow key={n.id} note={n} />
            ))}
          </ul>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <Button
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - CENTER_PAGE_SIZE))}
            >
              ← Newer
            </Button>
            <Button
              size="sm"
              disabled={offset + page.notes.length >= page.total}
              onClick={() => setOffset(offset + CENTER_PAGE_SIZE)}
            >
              Older →
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid="household-note-filter-chip"
      style={{
        fontSize: 12.5,
        padding: "4px 10px",
        borderRadius: 999,
        cursor: "pointer",
        border: `1px solid ${active ? "var(--blue)" : "var(--mid-gray)"}`,
        background: active ? "var(--blue)" : "var(--bg-0)",
        color: active ? "#fff" : "var(--navy)",
      }}
    >
      {label} <span style={{ opacity: 0.75 }}>{count}</span>
    </button>
  );
}

// "Showing 1–25 of 148" — the honest statement of what is on screen
// versus what exists. `total` is a COUNT(*) from the server, never the
// length of the array above it (CLAUDE.md accuracy rules 1 and 3).
function rangeLine(page: HouseholdNotesPage): string {
  const from = page.offset + 1;
  const to = page.offset + page.notes.length;
  if (page.total <= page.notes.length && page.offset === 0) {
    return `${page.total} ${page.total === 1 ? "note" : "notes"}, newest first.`;
  }
  return `Showing ${from}–${to} of ${page.total}, newest first.`;
}

// Origin detail arrives as stored data — an outcome slug ("won"), a
// policy number, a cycle anchor date. Slugs get title-cased; everything
// else passes through untouched.
function humanizeDetail(raw: string): string {
  if (/^[a-z][a-z_]*$/.test(raw)) {
    return raw
      .split("_")
      .map((w) => (w.length > 0 ? (w[0] as string).toUpperCase() + w.slice(1) : ""))
      .join(" ");
  }
  return raw;
}

function formatNoteDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
